<script setup>
import {useStore} from "vuex";
import {computed, onMounted, watch} from "vue";
import BackButton from "@/components/global-themed/buttons/BackButton.vue";
import ComparisonTypeSelector from "@/components/competition/partials/ComparisonTypeSelector.vue";
import ClearAllButton from "@/components/competition/partials/compare/ClearAllButton.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import CompareTableHeader from "@/components/competition/partials/compare/CompareTableHeader.vue";
import CompareKeywordsTableBody from "@/components/competition/partials/compare/CompareKeywordsTableBody.vue";
import CompareBacklinksTableBody from "@/components/competition/partials/compare/CompareBacklinksTableBody.vue";
import useWebsite from "@/composables/useWebsite";

const props = defineProps({
  competitorId: {
    type: Number,
    required: false,
    default: undefined,
  }
})

const store = useStore();
const queryParams = new URLSearchParams(location.search);

const competitors = computed(() => store.getters['competitionV2/competitors']);

onMounted(() => {
  store.dispatch('competitionV2/fetchWebsiteCompareData');
  store.dispatch('competitionV2/fetchCompetitors');
  if (queryParams.get('activeTab') === 'backlink') {
    store.dispatch('competitionV2/setComparisonType', 'backlink');
  }
});

const { website } = useWebsite();

const isKeywordView = computed(() => store.getters['competitionV2/comparisonType'] === 'keyword')

const competitorId = computed(() => props.competitorId ? parseInt(props.competitorId) : undefined);

watch(competitors, function (newCompetitors) {
  if (!newCompetitors) {
    return;
  }

  if (competitorId.value) {
    const competitor = competitors.value.find(competitor => competitor.id === competitorId.value);
    if (competitor) {
      store.dispatch('competitionV2/setCompareCompetitor', {
        column: 1,
        competitor,
      });
    }
  }

  const competitorsParam = queryParams.get('competitors');

  if (competitorsParam && competitors.value.length > 0) {
    const competitorIds = competitorsParam
      .split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id))
      .slice(0, 2);

    competitorIds.forEach((competitorId, index) => {
      const competitor = competitors.value.find(competitor => competitor.id === competitorId);
      if (competitor) {
        store.dispatch('competitionV2/setCompareCompetitor', {
          column: index + 1, // Columns 1 and 2
          competitor,
        });
      }
    });
  }
}, {
  immediate: true,
})

const compareCompetitors = computed(() => store.getters['competitionV2/compareCompetitors']);

watch(compareCompetitors, (newCompareCompetitors) => {
  window.mixpanel('Competitor: Compare competitors', {
    competitor_urls: newCompareCompetitors.filter(competitor => competitor !== undefined)
      .map(competitor => competitor?.simpleUrl),
    website_id: website.value?.id,
  });
}, {
  immediate: true,
  deep: true,
})

</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-justify-between tw-items-center tw-mb-8">
      <div class="tw-flex tw-flex-col">
        <h1 class="tw-text-4.5xl tw-text-font-primary tw-font-bold tw-font-sans">
          Competitor Analysis
        </h1>

        <p class="tw-text-font-secondary tw-text-base">
          Compare your own website against your top competitors using important keyword and backlink metrics.
        </p>
        <p class="tw-text-font-secondary tw-text-base">
          Quickly see how your website performs in comparison and what areas you need to work on.
        </p>
      </div>

      <BackButton href="/competition" />
    </div>
    <div class="tw-bg-white tw-rounded-lg tw-p-8">
      <div class="tw-flex tw-w-full tw-justify-between">
        <ComparisonTypeSelector />

        <ClearAllButton class="tw-w-auto" />
      </div>

      <DataTable>
        <CompareTableHeader />
        <CompareKeywordsTableBody v-if="isKeywordView" />
        <CompareBacklinksTableBody v-else />
      </DataTable>
    </div>
  </div>
</template>
